import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Phone, MessageCircle, Clock, GraduationCap, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { API, whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL, SOCIAL_PROFILES, ogFromAsset } from "@/lib/seo";
import { LazyMap } from "@/components/LazyMap";
import { GALLERY_ALTS } from "@/lib/galleryAlts";

export const CENTER_PAGES = {
  "autoescuela-plaza-america": {
    name: "Autoescuelas Faro - Plaza América (Vigo)",
    short: "Plaza América",
      seoDescription: "Autoescuela en Plaza América (Vigo): C. C. 3 Plaza América, Local 12. Centro autorizado DGT para recuperación de puntos. Tel. 986 29 24 55.",
    titleLabel: "Plaza América (Vigo)",
    address: "Centro Comercial 3 Plaza América, Local 12, 36211 Vigo, Pontevedra",
    landline: "986 29 24 55",
    whatsapp: "34658790266",
    locality: "Vigo",
    postal: "36211",
    street: "Centro Comercial 3 Plaza América, Local 12",
    map_query: "Autoescuelas Faro, Centro Comercial 3 Plaza América, Vigo",
    lat: 42.225,
    lng: -8.736,
    area: ["Vigo", "As Travesas", "Balaídos", "Coia", "O Porriño", "Redondela"],
    hours: [["10:00", "13:30"], ["16:30", "20:00"]],
    extra: "Centro autorizado por la DGT para cursos de sensibilización y reeducación vial (recuperación de puntos y del permiso).",
    services: ["Permiso B (coche)", "Permisos de moto (AM, A1, A2, A)", "Curso de recuperación de puntos (1 día + online)", "Curso de recuperación del carnet (20 h)", "Test y teóricas presenciales"],
  },
  "autoescuela-coruxo": {
    name: "Autoescuelas Faro - Coruxo (Vigo)",
    short: "Coruxo",
      seoDescription: "Autoescuela en Coruxo (Vigo): Estrada Camposancos, 202. Aparcamiento propio y especialistas en permisos profesionales y CAP. Tel. 986 23 62 65.",
    titleLabel: "Coruxo (Vigo)",
    address: "Estrada Camposancos, 202, 36330 Vigo, Pontevedra",
    landline: "986 23 62 65",
    whatsapp: "34658790210",
    locality: "Vigo",
    postal: "36330",
    street: "Estrada Camposancos, 202",
    lat: 42.190333,
    lng: -8.789444,
    area: ["Coruxo", "Oia", "Saiáns", "Vigo"],
    hours: [["09:30", "13:30"], ["17:00", "20:00"]],
    extra: "Sede con aparcamiento propio, especializada en permisos profesionales y cursos CAP.",
    services: ["Permiso B (coche)", "Camión (C, C+E) y autobús (D)", "Cursos CAP inicial y continuo", "Remolques (B96, B+E)", "Teóricas y test presenciales"],
  },
  "autoescuela-panxon": {
    name: "Autoescuelas Faro - Panxón (Nigrán)",
    short: "Panxón",
      seoDescription: "Autoescuela en Panxón (Nigrán): Praza Jesús Espinosa, 1. Sácate el carnet sin desplazarte a Vigo: coche, moto y teóricas. Tel. 986 36 66 51.",
    titleLabel: "Panxón (Nigrán)",
    address: "Praza Jesús Espinosa, 1, 36340 Nigrán, Pontevedra",
    landline: "986 36 66 51",
    whatsapp: "34658790212",
    locality: "Nigrán",
    postal: "36340",
    street: "Praza Jesús Espinosa, 1",
    lat: 42.149444,
    lng: -8.823333,
    area: ["Panxón", "Praia América", "Val Miñor", "Nigrán", "Baiona"],
    hours: [["10:00", "13:00"], ["16:30", "19:30"]],
    extra: "Tu autoescuela en Panxón, a pie de playa, para sacarte el carnet sin desplazarte a Vigo.",
    services: ["Permiso B (coche)", "Permisos de moto", "Teóricas y test presenciales", "Matrícula para cursos de puntos en Vigo"],
  },
  "autoescuela-nigran": {
    name: "Autoescuelas Faro - Nigrán",
    short: "Nigrán",
      seoDescription: "Autoescuela en Nigrán: Rúa Canido, 2. Horarios pensados para estudiantes y trabajadores del Val Miñor. Tel. 986 36 84 24.",
    address: "Rúa Canido, 2, 36350 Nigrán, Pontevedra",
    landline: "986 36 84 24",
    whatsapp: "34658790213",
    locality: "Nigrán",
    postal: "36350",
    street: "Rúa Canido, 2",
    map_query: "Autoescuela Faro, Rúa Canido, 2, 36350 Nigrán",
    lat: 42.150972,
    lng: -8.795278,
    area: ["Nigrán", "Val Miñor", "Gondomar", "Baiona"],
    hours: [["10:00", "13:00"], ["16:30", "19:30"]],
    extra: "Sede en el centro de Nigrán con horarios pensados para estudiantes y trabajadores del Val Miñor.",
    services: ["Permiso B (coche)", "Permisos de moto", "Teóricas y test presenciales", "Matrícula para cursos de puntos en Vigo"],
  },
  "autoescuela-a-caniza": {
    name: "Autoescuelas Faro - A Cañiza",
    short: "A Cañiza",
      seoDescription: "Autoescuela en A Cañiza: C/ Villanueva, 20. Permisos B y moto, con más de 45 años de trayectoria en la comarca. Tel. 986 65 13 29.",
    address: "C/ Villanueva, 20, 36880 A Cañiza, Pontevedra",
    landline: "986 65 13 29",
    whatsapp: "34610344354",
    locality: "A Cañiza",
    postal: "36880",
    street: "C/ Villanueva, 20",
    lat: 42.21,
    lng: -8.27,
    area: ["A Cañiza", "O Covelo", "Arbo", "Crecente"],
    hours: [["10:00", "13:00"], ["17:00", "20:00"]],
    extra: "La autoescuela de referencia en A Cañiza y su comarca, avalada por la trayectoria de Autoescuelas Faro (más de 45 años en el sector desde 1980).",
    services: ["Permiso B (coche)", "Permisos de moto", "Teóricas y test presenciales", "Matrícula para cursos de puntos en Vigo"],
  },
};

export default function CenterPage() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const c = CENTER_PAGES[slug];

  useSeo(
    c
      ? {
          title: `Autoescuela en ${c.titleLabel || c.short} | Autoescuelas Faro`,
        description: c.seoDescription || c.extra,
          canonical: `${SITE_URL}/${slug}`,
          ogImage: ogFromAsset("/assets/sedes-autoescuelas-faro-vigo.webp"),
          ogImageAlt: `${c.name}, autoescuela en ${c.locality}`,
        }
      : {}
  );

  const [photos, setPhotos] = useState([]);
  const centerId = slug ? slug.replace("autoescuela-", "").replace(/-/g, "") : "";
  useEffect(() => {
    if (!centerId) return;
    axios
      .get(`${API}/centers`)
      .then((r) => {
        const match = r.data.find((x) => x.id === centerId);
        setPhotos((match && match.images) || []);
      })
      .catch(() => {});
  }, [centerId]);

  if (!c) return <Navigate to="/centros" replace />;

  const wa = whatsappLink(c.whatsapp, `Hola, me gustaría información de la autoescuela de ${c.short}`);

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid={`center-page-${slug}`}>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "EducationalOrganization"],
        "@id": `${SITE_URL}/${slug}#business`,
        name: c.name,
        image: `${SITE_URL}/assets/sedes-autoescuelas-faro-vigo.webp`,
        url: `${SITE_URL}/${slug}`,
        telephone: `+34${c.landline.replace(/\s/g, "")}`,
        email: "info@autoescuelasfaro.com",
        priceRange: "€€",
        address: { "@type": "PostalAddress", streetAddress: c.street, postalCode: c.postal, addressLocality: c.locality, addressRegion: "Pontevedra", addressCountry: "ES" },
        geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
        areaServed: c.area,
        sameAs: SOCIAL_PROFILES,
        ...(slug === "autoescuela-plaza-america" ? {
          identifier: {
            "@type": "PropertyValue",
            name: "Autorización DGT",
            value: "360024",
          },
        } : {}),
        openingHoursSpecification: c.hours.map(([opens, closes]) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens,
          closes,
        })),
        hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.map_query || c.address)}`,
        parentOrganization: { "@type": "Organization", name: "Autoescuelas Faro", url: SITE_URL },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Centros", item: `${SITE_URL}/centros` },
          { "@type": "ListItem", position: 3, name: c.short },
        ],
      }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Link to="/centros" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)]" data-testid="center-back">
          <ArrowLeft size={16} /> Todos los centros
        </Link>
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Autoescuela en {c.locality}</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">{c.name}</h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">{c.extra}</p>

        {photos.length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="center-page-photos">
            {photos.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={GALLERY_ALTS[src] || `Instalaciones de Autoescuelas Faro en ${c.short} (foto ${i + 1})`}
                width="800"
                height="600"
                className="w-full h-52 sm:h-60 object-cover rounded-2xl border border-[rgba(30,42,143,0.12)]"
                loading="lazy"
              />
            ))}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5 space-y-3">
            <div className="flex items-start gap-2 text-sm">
              <MapPin size={17} className="text-[var(--faro-red)] shrink-0 mt-0.5" />
              <span className="font-bold">{c.address}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={16} className="text-[var(--faro-blue)] shrink-0" />
              <a href={`tel:+34${c.landline.replace(/\s/g, "")}`} className="font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)]" data-testid="center-phone">{c.landline}</a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock size={16} className="text-[var(--faro-blue)] shrink-0" />
              <span data-testid="center-hours">Lunes a viernes · {c.hours[0][0].replace(/^0/, "")}–{c.hours[0][1]} y {c.hours[1][0]}–{c.hours[1][1]}</span>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <a href={wa} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-xs" data-testid="center-wa">
                <MessageCircle size={13} /> WhatsApp
              </a>
              <a href="/#contacto" className="btn-pill btn-outline text-xs">Pedir información <ArrowRight size={12} /></a>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] min-h-[220px]">
            {slug === "autoescuela-plaza-america" ? (<><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Autoescuelas Faro, Centro Comercial 3 Plaza América, Vigo")}`} target="_blank" rel="noopener noreferrer" className="block w-full h-full" aria-label="Abrir la ubicacion en Google Maps"><img src="/img/mapa-plaza-america.jpg" alt="Mapa de la zona de Plaza America en Vigo" width="1024" height="420" className="w-full h-full object-cover" /></a><span className="pointer-events-none absolute left-2 top-2 rounded bg-white px-3 py-1.5 text-xs font-medium text-[#202124] shadow">Abrir en Maps &#8599;</span><div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-3 -translate-y-full items-end gap-1"><svg width="28" height="36" viewBox="0 0 24 32" aria-hidden="true"><path d="M12 0C5.4 0 0 5.3 0 12c0 8.9 12 20 12 20s12-11.1 12-20C24 5.3 18.6 0 12 0z" fill="#EA4335" /><circle cx="12" cy="12" r="4.5" fill="#fff" /></svg><span className="mb-1 rounded bg-white/95 px-2 py-1 text-[11px] font-medium text-[#202124] shadow">Autoescuelas Faro - Plaza América</span></div><span className="absolute bottom-1 right-2 text-[10px] bg-white/80 px-1 rounded">&copy; OpenStreetMap contributors</span></>) : (<LazyMap
              title={`Mapa de ${c.name}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(c.map_query || c.address)}&output=embed`}
              className="min-h-[220px]"
              testid={`center-map-${slug}`}
            />)}
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <GraduationCap className="text-[var(--faro-red)]" size={26} /> Qué puedes hacer en este centro
          </h2>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {c.services.map((s) => (
              <li key={s} className="flex items-center gap-2 rounded-xl bg-[var(--faro-bg)] px-4 py-3 text-sm font-bold text-[var(--faro-blue-deep)]">
                <ShieldCheck size={15} className="text-[var(--faro-red)] shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="font-display font-black text-2xl sm:text-3xl">También te puede interesar</h2>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm font-bold">
            <Link to="/recuperacion-de-puntos-vigo" className="text-[var(--faro-blue)] underline underline-offset-2 hover:text-[var(--faro-red)]">Curso de recuperación de puntos (1 día + online) →</Link>
            <Link to="/curso-sensibilizacion-vigo" className="text-[var(--faro-blue)] underline underline-offset-2 hover:text-[var(--faro-red)]">Curso de recuperación del carnet (20 h) →</Link>
            <Link to="/proximos-cursos" className="text-[var(--faro-blue)] underline underline-offset-2 hover:text-[var(--faro-red)]">Próximas convocatorias →</Link>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
